import React, { useContext, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';

import { compareItems, areEqualShallow } from '../../utils/utils';
import { ListContext } from '../ListContext';
import { resolveListQueryVariables } from '../../utils/list/resolveListQueryVariables';

import { usePrevious, useDidUpdateEffect } from '../../utils/hooks';

export default (WrappedComponent) => {
	const ReturnedComponent = (props) => {
		const listContext = useContext(ListContext);

		const {
			variables,
			getAllItems,
			updateVariables,
			items,
			fetchMore,
			canUseFrontEndFunctionality,
			initialFilter,
			updateQuery,
		} = listContext;

		const {
			page,
			perPage,
			sortBy,
			sortDirection,
			sortByResolver,
			filter,
			destroyed,
			extraFields,
		} = variables || {};

		const otherVariables = useMemo(() => {
			if (!variables) {
				return {};
			}

			const restVariables = { ...variables };
			delete restVariables.page;
			delete restVariables.perPage;
			delete restVariables.sortBy;
			delete restVariables.sortDirection;
			delete restVariables.sortByResolver;
			delete restVariables.filter;
			delete restVariables.destroyed;
			delete restVariables.extraFields;

			return restVariables;
		}, [variables]);
		// Treat undefined perPage as a "show all" request so page/perPage are omitted.
		const shouldGetAllItems = useMemo(() => getAllItems || perPage === undefined, [getAllItems, perPage]);
		// Ensure page/perPage override any cached Apollo variables when showing all items.
		const getPaginationVariables = useCallback((nextPage = page, nextPerPage = perPage) => {
			if (shouldGetAllItems || nextPerPage === undefined) {
				return { page: undefined, perPage: undefined };
			}

			return { page: nextPage, perPage: nextPerPage };
		}, [page, perPage, shouldGetAllItems]);
		const resolveQueryVariables = useCallback((baseVariables) => {
			return resolveListQueryVariables({
				...otherVariables,
				...baseVariables,
				extraFields,
			});
		}, [extraFields, otherVariables]);
		// If the initial filter changes, re-filter the list query
		// NOTE: Changing initial props to withListQuery does not re-invoke the GraphQL query.
		// This is because state does not change in withListQuery. This may be preferable, or not.
		// This can be re-factored if required. For now, this bit of code seems good / reasonable.
		const previousInitialFilter = usePrevious(initialFilter);

		// NOTE 1: If you want to 'unset' or 'reset' some filter value,
		// i.e. go from { HCGCategory: red } to { HCGCategory: any },
		// simply use 'change Filter', and set the value to undefined:
		// { HCGCategory: undefined }.

		// NOTE 2: Reset page to zero. Prevents the following issue: if on a page which will no longer
		// exist after the addition of the filter, you remain on a page that shows items
		// but has no page number. (Note: in previous framework, we had this issue too - it
		// was never solved.) Also, it seems like reasonable behaviour (I think...)
		const filterItems = useCallback(newFilter => {
			const mergedFilter = { ...filter, ...newFilter };
			updateVariables({
				...(shouldGetAllItems ? {} : { page: 0 }),
				filter: mergedFilter,
			});
		}, [filter, shouldGetAllItems, updateVariables]);

		useDidUpdateEffect(() => {
			if (
				// if filter  references different
				initialFilter !== previousInitialFilter &&
				(
					// added additional check - shallow filter  comparison
					!initialFilter || !previousInitialFilter ||
					// comparing filters twice as some key may be missing from one of the filters so it wouldn't be accurate
					!areEqualShallow(initialFilter, previousInitialFilter) || !areEqualShallow(previousInitialFilter, initialFilter)
				)
			) {
				filterItemsWithFilterOverride(initialFilter);
			}
		}, [initialFilter, previousInitialFilter, filterItems]);

		useDidUpdateEffect(() => {
			fetchMore({
				variables: resolveQueryVariables({
					sortBy,
					sortDirection,
					...(filter ? { filter } : {}),
					...getPaginationVariables(),
					...(destroyed !== undefined ? { destroyed } : {}),
				}),
				updateQuery,
			});
		}, [page, filter, resolveQueryVariables]);

		// If you change perPage, update variables; however, also set page to 0. This makes sense / is the standard
		// expected behaviour, I think. (If not done, you can easily stay on some page which ends up with no items, once
		// the perPage limit is increased.

		// NOTE: Check for undefined and set to undefined if true. This handles when getting all items (we don't want to
		// set page to zero in that case, breaks back-end. See e.g. planogram product selector, which switches to favourites,
		// where we want to get all items. If this proves problematic, can pass some prop like doNotResetPage)

		useDidUpdateEffect(() => {
			const nextPage = page === undefined ? page : 0;

			fetchMore({
				variables: resolveQueryVariables({
					...getPaginationVariables(nextPage, perPage),
					sortBy,
					sortDirection,
					...(filter ? { filter } : {}),
					...(destroyed !== undefined ? { destroyed } : {}),
				}),
				updateQuery,
			});
		}, [perPage, resolveQueryVariables]);

		useDidUpdateEffect(() => {
			fetchMore({
				variables: resolveQueryVariables({
					sortBy,
					sortDirection,
					...(filter ? { filter } : {}),
					...getPaginationVariables(),
					...(destroyed !== undefined ? { destroyed } : {}),
				}),
				updateQuery,
			});
		}, [destroyed, resolveQueryVariables]);

		useDidUpdateEffect(() => {
			if (canUseFrontEndFunctionality) {
				// Front end sort
				// DO NOTHING! Front-end sort is
				// most simply and easily and side-effect-free done below.
			}
			else {
				// Back end sort
				fetchMore({
					variables: resolveQueryVariables({
						sortBy,
						sortDirection,
						...getPaginationVariables(),
						...(filter ? { filter } : {}),
						...(destroyed !== undefined ? { destroyed } : {}),
					}),
					updateQuery,
				});
			}
		}, [sortBy, sortDirection, resolveQueryVariables]);

		const changePage = useCallback(nextPage => {
			updateVariables({ page: nextPage });
		}, [updateVariables]);

		const changePageLimit = useCallback(perPageNew => {
			if (perPageNew === undefined) {
				updateVariables({ page: undefined, perPage: undefined });
				return;
			}

			updateVariables({
				perPage: perPageNew,
				...(page === undefined ? { page: 0 } : {}),
			});
		}, [page, updateVariables]);

		const changePageLimitAndResetPage = useCallback(perPageNew => {
			if (perPageNew === undefined) {
				updateVariables({ page: undefined, perPage: undefined });
				return;
			}

			updateVariables({
				perPage: perPageNew,
				...(getAllItems ? {} : { page: 0 }),
			});
		}, [updateVariables, getAllItems]);

		const filterItemsWithFilterOverride = useCallback(newFilter => {
			const mergedFilter = newFilter || {};

			// keep the query filter
			if (filter && filter.query) {
				mergedFilter.query = filter.query;
			}

			updateVariables({
				...(shouldGetAllItems ? {} : { page: 0 }),
				filter: mergedFilter,
			});
		}, [shouldGetAllItems, filter, updateVariables]);

		const changeFilter = useCallback(newFilter => {
			return filterItems(newFilter);
		}, [filterItems]);

		const changeQueryFilter = useCallback(newQueryString => {
			return filterItems({ query: newQueryString });
		}, [filterItems]);

		const changeQueryFilterDebounced = useMemo(() => debounce(changeQueryFilter, 300), [changeQueryFilter]);

		// Same as the above, except without page reset. Uncommonly used, since we normally want page reset.
		// Used e.g. when you are getting all items, so want page to be undefined.
		const filterItemsWithoutPageReset = useCallback(newFilter => {
			const mergedFilter = { ...filter, ...newFilter };
			updateVariables({
				filter: mergedFilter,
			});
		}, [filter, updateVariables]);

		const changeQueryFilterWithoutPageReset = useCallback(
			newQueryString => filterItemsWithoutPageReset({ query: newQueryString }),
			[filterItemsWithoutPageReset],
		);

		const changeQueryFilterDebouncedWithoutPageReset = useMemo(
			() => debounce(changeQueryFilterWithoutPageReset, 300),
			[changeQueryFilterWithoutPageReset],
		);

		const changeSort = useCallback((newSortBy, newSortByResolver) => {
			let newSortDirection = -1;
			if (sortBy === newSortBy) {
				newSortDirection = -1 * sortDirection;
			}

			updateVariables({
				sortDirection: newSortDirection,
				sortBy: newSortBy,
				sortByResolver: newSortByResolver,
			});
		}, [updateVariables, sortBy, sortDirection]);

		const changeSortDirection = useCallback(newSortDirection => {
			updateVariables({
				sortDirection: newSortDirection,
			});
		}, [updateVariables]);

		const changeDestroyed = useCallback(newDestroyed => {
			updateVariables({
				destroyed: newDestroyed,
			});
		}, [updateVariables]);

		const sortedItems = useMemo(() => {
			if (canUseFrontEndFunctionality) {
				// Front-end sort
				return !items ? items : [...items].sort(compareItems(sortBy, sortDirection, sortByResolver));
			}

			return items;
		}, [canUseFrontEndFunctionality, items, sortBy, sortDirection, sortByResolver]);

		const context = useMemo(() => ({
			...listContext,	// Adds to the old list context

			// Do action directly
			filterItems,

			loadingInitial: listContext.loadingInitial || sortedItems === null,

			changePage,
			changePageLimit,
			changePageLimitAndResetPage,
			changeFilter,
			filterItemsWithFilterOverride,
			changeQueryFilter,
			filterItemsWithoutPageReset,
			changeQueryFilterDebounced,
			changeQueryFilterDebouncedWithoutPageReset,
			changeSort,
			changeSortDirection,
			changeDestroyed,

			items: sortedItems === null ? [] : sortedItems,
			hasPagination: !getAllItems,
		}), [listContext, filterItems, changePage, changePageLimit, changeFilter, filterItemsWithFilterOverride, filterItemsWithoutPageReset, changeQueryFilter, changeDestroyed, changeSortDirection,
			changeQueryFilterDebounced, changeQueryFilterDebouncedWithoutPageReset, changeSort, sortedItems, getAllItems, changePageLimitAndResetPage]);

		return (
			<ListContext.Provider value={context}>
				<WrappedComponent {...props} />
			</ListContext.Provider>
		);
	};

	return ReturnedComponent;
};
